import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Download } from 'lucide-react';
import axios from 'axios';
import ModernCard from '../components/ModernCard';
import { API_ENDPOINTS } from '../config/api';
import './PastPapersPage.css';

const PastPapersPage = () => {
  const { unitName } = useParams();
  const [originalUnitName, setOriginalUnitName] = useState('');
  const [pastPaperFiles, setPastPaperFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // First get all units to find the original name
    const findOriginalUnitName = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.UNITS);
        const units = response.data;
        const originalName = units.find(unit => {
          const slug = unit.toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/[\s_-]+/g, '-')
            .replace(/^-+|-+$/g, '');
          return slug === unitName;
        });
        
        if (originalName) {
          setOriginalUnitName(originalName);
          fetchPastPaperFiles(originalName);
        } else {
          setError('Unit not found');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error finding unit name:', err);
        setError('Failed to load unit. Please try again.');
        setLoading(false);
      }
    };

    findOriginalUnitName();
  }, [unitName]);

  const fetchPastPaperFiles = async (unitName) => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.UNIT_FILES(unitName));
      const pastPapers = response.data.files.filter(file => file.type === 'past-paper');
      setPastPaperFiles(pastPapers);
      setError(null);
    } catch (err) {
      console.error('Error fetching past papers:', err);
      setError('Failed to load past papers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (file, event) => {
    event.preventDefault();
    const downloadUrl = `${process.env.REACT_APP_API_URL}${file.download_url}`;
    window.open(downloadUrl, '_blank');
  };

  if (loading) {
    return (
      <div className="past-papers-page">
        <div className="page-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading past papers...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="past-papers-page">
        <div className="page-container">
          <div className="error-state">
            <h3>Error Loading Past Papers</h3>
            <p>{error}</p>
            <button className="retry-button" onClick={() => fetchPastPaperFiles(originalUnitName)}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="past-papers-page">
      <div className="page-container">
        {/* Header */}
        <header className="page-header">
          <Link to={`/unit/${unitName}`} className="back-button">
            <ArrowLeft size={20} />
            <span>Back to {originalUnitName}</span>
          </Link>
          <h1 className="page-title">Past Papers</h1>
          <p className="page-subtitle">{originalUnitName} - Past Examination Papers</p>
        </header>

        {/* Past Papers Files */}
        {pastPaperFiles.length > 0 ? (
          <div className="files-grid">
            {pastPaperFiles.map((file) => (
              <ModernCard
                key={file.name}
                icon={FileText}
                title={file.name}
                description="Past examination papers"
                buttonText={
                  <>
                    <Download size={16} />
                    Download
                  </>
                }
                onClick={(event) => handleDownload(file, event)}
                variant="file"
                className="file-card"
              />
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <FileText size={48} />
            <h3>No Past Papers Found</h3>
            <p>No past examination papers are available for this unit.</p>
            <Link to={`/unit/${unitName}`} className="back-link">
              Back to Unit
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default PastPapersPage;
